import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle, Loader2, XCircle, BookOpen, Zap, ShoppingCart, Sparkles, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, refreshUser, user } = useAuth();
  const [status, setStatus] = useState('loading');
  const [attempts, setAttempts] = useState(0);
  const [upsell, setUpsell] = useState(null);
  const [upsellLoading, setUpsellLoading] = useState(false);

  const sessionId = searchParams.get('session_id');
  const paymentType = searchParams.get('type');
  const courseId = searchParams.get('course_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('error');
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        await refreshUser();
        fetchUpsell();
        return;
      } else if (response.data.status === 'expired') {
        setStatus('error');
        return;
      }

      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  const fetchUpsell = async () => {
    try {
      const response = await axios.get(`${API}/courses/upsell?exclude_id=${courseId || ''}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.upsell) {
        setUpsell(response.data.upsell);
      }
    } catch (error) {
      console.error('Upsell fetch error:', error);
    }
  };

  const handleUpsellPurchase = async () => {
    if (!upsell) return;
    setUpsellLoading(true);
    try {
      const response = await axios.post(`${API}/checkout/purchase-upsell`, {
        course_id: upsell.id,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Upsell purchase error:', error);
      setUpsellLoading(false);
    }
  };

  const isInstructorPayment = paymentType === 'instructor';
  const isCoursePayment = paymentType === 'course';

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-lime-50 flex items-center justify-center p-4" data-testid="payment-success-page">
      <div className="w-full max-w-lg space-y-6">
        <Card className="border-slate-200 shadow-xl">
          <CardContent className="pt-8 pb-8 text-center">
            {status === 'loading' && (
              <>
                <Loader2 className="w-16 h-16 text-primary animate-spin mx-auto mb-6" />
                <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                  Processing Payment
                </h1>
                <p className="text-slate-600 mb-8">
                  Please wait while we confirm your payment...
                </p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                  Payment Successful!
                </h1>
                
                {isInstructorPayment ? (
                  <>
                    <div className="flex items-center justify-center gap-2 text-primary mb-4">
                      <Zap className="w-5 h-5" />
                      <span className="font-medium">Instructor Plan Activated</span>
                    </div>
                    <p className="text-slate-600 mb-8">
                      Your instructor subscription is now active. Start creating courses and earning money!
                    </p>
                    <div className="space-y-3">
                      <Button 
                        className="w-full bg-primary hover:bg-primary/90"
                        onClick={() => navigate('/instructor/courses/new')}
                        data-testid="create-course-btn"
                      >
                        Create Your First Course
                      </Button>
                      <Button 
                        variant="outline"
                        className="w-full"
                        onClick={() => navigate('/instructor')}
                        data-testid="go-dashboard-btn"
                      >
                        Go to Dashboard
                      </Button>
                    </div>
                  </>
                ) : isCoursePayment ? (
                  <>
                    <div className="flex items-center justify-center gap-2 text-primary mb-4">
                      <BookOpen className="w-5 h-5" />
                      <span className="font-medium">Course Purchased</span>
                    </div>
                    <p className="text-slate-600 mb-8">
                      You now have lifetime access to this course. Start learning now!
                    </p>
                    <div className="space-y-3">
                      <Button 
                        className="w-full bg-primary hover:bg-primary/90"
                        onClick={() => navigate(`/learn/${courseId}`)}
                        data-testid="start-course-btn"
                      >
                        Start Learning
                      </Button>
                      <Button 
                        variant="outline"
                        className="w-full"
                        onClick={() => navigate('/dashboard')}
                        data-testid="go-dashboard-btn"
                      >
                        Go to Dashboard
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-slate-600 mb-8">
                      Thank you for your purchase.
                    </p>
                    <Button 
                      className="w-full bg-primary hover:bg-primary/90"
                      onClick={() => navigate(user?.role === 'instructor' ? '/instructor' : '/dashboard')}
                    >
                      Go to Dashboard
                    </Button>
                  </>
                )}
              </>
            )}

            {status === 'error' && (
              <>
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <XCircle className="w-10 h-10 text-red-600" />
                </div>
                <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                  Payment Failed
                </h1>
                <p className="text-slate-600 mb-8">
                  Something went wrong with your payment. Please try again or contact support.
                </p>
                <div className="space-y-3">
                  <Button 
                    className="w-full bg-primary hover:bg-primary/90"
                    onClick={() => navigate(isInstructorPayment ? '/instructor/pricing' : '/courses')}
                    data-testid="try-again-btn"
                  >
                    Try Again
                  </Button>
                  <Button 
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate('/')}
                  >
                    Go Home
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Upsell Card */}
        {status === 'success' && upsell && (
          <Card className="border-amber-200 shadow-lg overflow-hidden" data-testid="upsell-card">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-2 flex items-center justify-center gap-2">
              <Sparkles className="w-4 h-4 text-white" />
              <span className="text-white font-semibold text-sm">Exclusive One-Time Offer — 30% Off!</span>
            </div>
            <CardContent className="p-5">
              <div className="flex gap-4 items-start">
                {upsell.thumbnail ? (
                  <img src={upsell.thumbnail} alt={upsell.title} className="w-20 h-20 rounded-lg object-cover shrink-0" />
                ) : (
                  <div className="w-20 h-20 rounded-lg bg-gradient-to-br from-primary/20 to-purple-100 flex items-center justify-center shrink-0">
                    <BookOpen className="w-8 h-8 text-primary/60" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-900 text-sm leading-tight line-clamp-2">{upsell.title}</h3>
                  {upsell.instructor_name && (
                    <p className="text-xs text-slate-500 mt-1">by {upsell.instructor_name}</p>
                  )}
                  <div className="flex items-baseline gap-2 mt-2">
                    <span className="text-lg font-bold text-green-700">${upsell.discount_price}</span>
                    <span className="text-sm text-slate-400 line-through">${upsell.original_price}</span>
                    <Badge className="bg-red-100 text-red-700 text-[10px] px-1.5">-30%</Badge>
                  </div>
                </div>
              </div>
              <Button
                className="w-full mt-4 bg-amber-500 hover:bg-amber-600 text-white h-11"
                onClick={handleUpsellPurchase}
                disabled={upsellLoading}
                data-testid="upsell-buy-btn"
              >
                {upsellLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ShoppingCart className="w-4 h-4 mr-2" />
                )}
                Grab This Deal
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <p className="text-center text-xs text-slate-400 mt-2">This offer won't be shown again</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
